import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { User, signInWithCustomToken } from 'firebase/auth';
import firebaseApp from '../firebase-app';
import { POPATypes } from '../api/popa';
import { getItem, removeItem, setItem } from '../utils/local-storage';
import POPA from '../api/popa';
import { TeamPermissions, TeamRoles } from '../types';
import { FS_PATH_BACKEND_AUTH_USERS } from '../constants';
import { doc, onSnapshot } from 'firebase/firestore';
import _ from 'lodash';
import Loading from '../components/loading';

const STORAGE_TOKEN_KEY = 'TOKEN_DATA';

export interface AuthContextValues {
  updateTokenData: (tokenData: POPATypes.TokensResponseData) => void;
  logout: () => Promise<void>;
  tokens: POPATypes.TokensResponseData | null;
  isSuperAdmin: boolean;
  isStatsAdmin: boolean;
  isTeamAdmin: boolean;
  currentUser: User | null;
  loadingCurrentUser: boolean;
  teamPermissions: TeamPermissions;
  hasTeamAccess: (teamAbbrv: string) => boolean;
  hasTeamRole: (teamAbbrv: string, role: TeamRoles) => boolean;
}

const AuthContext = React.createContext<AuthContextValues>({
  tokens: null,
  updateTokenData: () => {},
  logout: async () => {},
  currentUser: null,
  isSuperAdmin: false,
  isStatsAdmin: false,
  isTeamAdmin: false,
  loadingCurrentUser: false,
  teamPermissions: {},
  hasTeamAccess: () => false,
  hasTeamRole: () => false,
});

export const useUserAuth = () => useContext(AuthContext);

interface Props {
  children: React.ReactNode;
}

type UserClaims = {
  email: string;
  isBackendUser: boolean;
} & PermissionClaims;

type PermissionClaims = {
  teams: TeamPermissions;
  superadmin: boolean;
  statsadmin: boolean;
};

function hasPermissionsChanged(newClaims: PermissionClaims, oldClaims: PermissionClaims): boolean {
  if (newClaims.superadmin !== oldClaims.superadmin) {
    return true;
  }

  if (newClaims.statsadmin !== oldClaims.statsadmin) {
    return true;
  }

  return !_.isEqual(newClaims.teams, oldClaims.teams);
}

async function refreshTokens(
  tokenData: POPATypes.TokensResponseData,
  setTokens: (tokenData: POPATypes.TokensResponseData) => void,
  updateTokenData: (tokenData: POPATypes.TokensResponseData) => void,
  clearTokenData: () => void
): Promise<void> {
  try {
    const refreshedTokenData = await POPA.getFullLoginByTokens(
      tokenData.accessToken,
      tokenData.refreshToken
    );
    updateTokenData(refreshedTokenData);
    setTokens(refreshedTokenData);
  } catch (error) {
    console.log('Refreshing tokens failed!', error);
    clearTokenData();
  }
}

async function initTokens(
  setTokens: (tokenData: POPATypes.TokensResponseData) => void,
  updateTokenData: (tokenData: POPATypes.TokensResponseData) => void,
  clearTokenData: () => void
): Promise<void> {
  // Load local stored token data and refresh if expired
  const localTokenData: POPATypes.TokensResponseData = getItem(STORAGE_TOKEN_KEY);
  if (localTokenData?.accessToken) {
    const decodedAccessToken = jwtDecode(localTokenData.accessToken);
    if (decodedAccessToken.exp && decodedAccessToken.exp * 1000 < Date.now()) {
      await refreshTokens(localTokenData, setTokens, updateTokenData, clearTokenData);
    } else {
      setTokens(localTokenData);
    }
  } else {
    clearTokenData();
  }
}

export const AuthProvider = ({ children }: Props) => {
  const [tokens, setTokens] = useState<POPATypes.TokensResponseData | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentUserClaims, setCurrentUserClaims] = useState<UserClaims | null>(null);
  const [loadingCurrentUser, setLoadingCurrentUser] = useState<boolean>(true);

  const updateTokenData = useCallback((tokenData: POPATypes.TokensResponseData) => {
    setItem(STORAGE_TOKEN_KEY, tokenData);
  }, []);

  const clearTokenData = useCallback(async () => {
    removeItem(STORAGE_TOKEN_KEY);
    setCurrentUser(null);
    setTokens(null);
    setLoadingCurrentUser(false);
  }, []);

  const logout = useCallback(async () => {
    firebaseApp.logEvent('logout');
    try {
      if (tokens) {
        const redirectUrl = await POPA.getRedirectToLogout(tokens.idToken);
        removeItem(STORAGE_TOKEN_KEY);
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error('Signing out user failed', error);
    }
  }, [tokens]);

  // Initial load
  useEffect(() => {
    initTokens(setTokens, updateTokenData, clearTokenData);
  }, [setTokens, updateTokenData, clearTokenData]);

  // Auth with firebase
  useEffect(() => {
    async function authenticate(firebaseCustomToken: string) {
      try {
        const res = await signInWithCustomToken(firebaseApp.auth, firebaseCustomToken);
        const claims = (await res.user.getIdTokenResult()).claims as UserClaims;
        setCurrentUser(res.user);
        setCurrentUserClaims(claims);
        setLoadingCurrentUser(false);
      } catch (error) {
        clearTokenData();
      }
    }
    if (tokens) {
      if (tokens?.firebaseCustomToken) {
        authenticate(tokens?.firebaseCustomToken);
      } else {
        clearTokenData();
      }
    }
  }, [tokens, clearTokenData]);

  // Get user claims
  useEffect(() => {
    if (tokens && currentUser && currentUserClaims) {
      const docRef = doc(firebaseApp.db, `${FS_PATH_BACKEND_AUTH_USERS}/${currentUser.uid}`);
      const unsubcribe = onSnapshot(docRef, async (documentSnapshot) => {
        if (documentSnapshot.exists()) {
          const userData = documentSnapshot.data();
          const permissionClaims = {
            superadmin: userData?.superadmin || false,
            statsadmin: userData?.statsadmin || false,
            teams: userData?.teams || {},
          } satisfies PermissionClaims;
          if (hasPermissionsChanged(permissionClaims, currentUserClaims)) {
            await refreshTokens(tokens, setTokens, updateTokenData, clearTokenData);
          }
        }
      });

      return unsubcribe;
    }
  }, [currentUser, clearTokenData, tokens, updateTokenData, currentUserClaims]);

  const hasTeamAccess = useCallback(
    (teamAbbrv: string) => {
      return (
        currentUserClaims?.superadmin ||
        currentUserClaims?.statsadmin ||
        !!currentUserClaims?.teams[teamAbbrv]
      );
    },
    [currentUserClaims]
  );

  const hasTeamRole = useCallback(
    (teamAbbrv: string, role: TeamRoles) => {
      return (
        currentUserClaims?.superadmin ||
        currentUserClaims?.teams[teamAbbrv]?.includes(role) ||
        false
      );
    },
    [currentUserClaims]
  );

  const contextValue = useMemo(
    () => ({
      tokens,
      updateTokenData,
      isSuperAdmin: currentUserClaims?.superadmin || false,
      isStatsAdmin: currentUserClaims?.statsadmin || false,
      isTeamAdmin: Object.keys(currentUserClaims?.teams || {}).length > 0,
      currentUser,
      loadingCurrentUser,
      logout,
      teamPermissions: currentUserClaims?.teams || {},
      hasTeamAccess,
      hasTeamRole,
    }),
    [
      tokens,
      updateTokenData,
      currentUser,
      currentUserClaims,
      loadingCurrentUser,
      logout,
      hasTeamAccess,
      hasTeamRole,
    ]
  );

  if (loadingCurrentUser) {
    return <Loading />;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
